// @djsem/render-state — Lighting Scene Engine (SIMULATION ONLY) (Prompt 11A).
//
// Resolves cue/chapter/semantic/stem/energy state into a renderer-neutral lighting runtime
// state and DRY-RUN adapter events. SIMULATION ONLY: no DMX/Art-Net/sACN/laser/timecode/
// hardware protocol code exists here (rules 3, 7, 8). Safety always wins (rule 9): emergency
// safe mode and blackout override everything; high-intensity/laser needs venue permission.
//
// Note: this is the richer Prompt-11A lighting model (venue rigs + adapter events). The older
// render-state `LightingScene` (scenes.ts) is a separate, simpler show-direction contract and
// is intentionally left untouched.

import {
  vArray, vBoolean, vNumber, vObject, vString, vUnion, type Validator,
  type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult,
} from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** Simulated lighting fixture group category (all simulation-only; no real hardware protocol). */
export type DJSEMFixtureGroupType =
  | "wash" | "beam" | "moving_head" | "strobe" | "blinder" | "pixel" | "booth"
  | "crowd" | "atmosphere" | "gobo" | "projection_brightness" | "led_wall" | "laser_simulation";

/** A named group of simulated fixtures within a venue zone. */
export interface DJSEMFixtureGroup {
  id: string; label: string; groupType: DJSEMFixtureGroupType; zoneId: string;
  capabilities: string[]; simulationOnly: boolean; safetyNotes: string[];
}

/** A physical/logical area of a venue (stage, dancefloor, booth, etc.). */
export interface DJSEMVenueZone {
  id: string; label: string;
  zoneType: "stage" | "booth" | "dancefloor" | "crowd" | "ceiling" | "wall" | "screen" | "other";
}

/** A simulated production rig for a venue: its zones, fixture groups, and safety profile. */
export interface DJSEMVenueProductionRig {
  id: string; label: string;
  profileType: "bedroom_streamer" | "small_bar" | "small_club" | "projection_venue" | "led_wall_venue" | "hybrid_dj_vj";
  zones: DJSEMVenueZone[];
  fixtureGroups: DJSEMFixtureGroup[];
  safetyProfile: { allowHighIntensity: boolean; allowStrobeSimulation: boolean; allowLaserSimulation: boolean; notes: string[] };
}

/** Resolved simulated state (on/off, intensity, color, motion) of one fixture group. */
export interface DJSEMFixtureGroupState {
  fixtureGroupId: string; enabled: boolean; intensity: number;
  colorPalette?: string[];
  motionMode?: "none" | "slow" | "medium" | "fast" | "rhythmic" | "rising" | "impact";
  effectMode?: string; triggerSource?: string;
}

/** A lighting scene definition: which chapters/cues/semantics/stems trigger it and its fixture states. */
export interface DJSEMLightingScene {
  id: string; label: string; description: string;
  chapterUses: string[]; semanticUses: string[]; cueUses: string[]; stemDrivers: string[];
  fixtureGroupStates: DJSEMFixtureGroupState[];
  safetyLevel: "safe" | "standard" | "high_intensity" | "restricted";
}

/** Resolved runtime lighting state produced by {@link LightingSceneEngineSimulation.resolveLightingState}. */
export interface DJSEMLightingRuntimeState {
  stateId: string; sessionId?: string; resolvedAt: string;
  status: "idle" | "active" | "safe" | "blackout" | "degraded";
  activeSceneId?: string;
  fixtureGroupStates: DJSEMFixtureGroupState[];
  warnings: string[]; sourceTrace: Record<string, unknown>;
}

/** A dry-run-only adapter-ready event describing a lighting state change, for downstream adapters to consume. */
export interface DJSEMLightingAdapterEvent {
  eventId: string; createdAt: string;
  eventType: "lighting.scene.resolved" | "lighting.blackout.enabled" | "lighting.safe_mode.enabled" | "lighting.fixture_group.updated" | "lighting.adapter.dry_run";
  dryRunOnly: true; payload: unknown; sourceTrace: Record<string, unknown>;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link LightingSceneEngineSimulation.resolveLightingState}. */
export interface ResolveLightingStateInput {
  scenes: DJSEMLightingScene[]; rig?: DJSEMVenueProductionRig; resolvedAt?: string; sessionId?: string;
  emergencySafeMode?: boolean; blackout?: boolean;
  manualCueSceneId?: string; explicitCueSceneId?: string;
  impactEvent?: boolean; stemOnset?: boolean;
  chapterSceneId?: string; semanticMatchIds?: string[]; globalEnergy?: number;
}
/** Input for {@link LightingSceneEngineSimulation.resolveBlackout}. */
export interface ResolveBlackoutInput { resolvedAt?: string; sessionId?: string; }
/** Input for {@link LightingSceneEngineSimulation.resolveSafeMode}. */
export interface ResolveLightingSafeModeInput { resolvedAt?: string; sessionId?: string; reason?: string; }
/** Input for {@link LightingSceneEngineSimulation.applyVenueRig}. */
export interface ApplyVenueRigInput { state: DJSEMLightingRuntimeState; rig: DJSEMVenueProductionRig; }
/** Input for {@link LightingSceneEngineSimulation.createAdapterReadyEvents}. */
export interface CreateLightingAdapterEventsInput { state: DJSEMLightingRuntimeState; createdAt?: string; }

const AT = "1970-01-01T00:00:00.000Z";
const clampUnit = (n: number | undefined, d = 0) => Math.max(0, Math.min(1, typeof n === "number" && Number.isFinite(n) ? n : d));

// ---- validators -------------------------------------------------------------

const vFixtureGroupState: Validator = vObject(
  { fixtureGroupId: vString({ min: 1 }), enabled: vBoolean, intensity: vNumber({ min: 0, max: 1 }) },
  { colorPalette: vArray(vString({})), motionMode: vString({ enum: ["none", "slow", "medium", "fast", "rhythmic", "rising", "impact"] }), effectMode: vString({}), triggerSource: vString({}) },
);
/** Validator for {@link DJSEMLightingScene}. */
export const vLightingSceneSim: Validator<DJSEMLightingScene> = vObject(
  {
    id: vString({ min: 1 }), label: vString({ min: 1 }), description: vString({}),
    chapterUses: vArray(vString({})), semanticUses: vArray(vString({})), cueUses: vArray(vString({})), stemDrivers: vArray(vString({})),
    fixtureGroupStates: vArray(vFixtureGroupState),
    safetyLevel: vString({ enum: ["safe", "standard", "high_intensity", "restricted"] }),
  },
) as Validator<DJSEMLightingScene>;

// Reject any real hardware-protocol vocabulary on a rig/scene (rule 3).
const PROTOCOL = /(dmx|artnet|art-net|sacn|e131|pangolin|fb4|ilda|midi|osc|universe|channel\d|timecode|smpte)/i;
/**
 * Validator for {@link DJSEMVenueProductionRig}. Rejects any fixture group not marked
 * `simulationOnly: true` and any capability naming a real hardware protocol.
 */
export const vVenueRig: Validator<DJSEMVenueProductionRig> = (v, path = "$") => {
  const base = vObject(
    {
      id: vString({ min: 1 }), label: vString({ min: 1 }),
      profileType: vString({ enum: ["bedroom_streamer", "small_bar", "small_club", "projection_venue", "led_wall_venue", "hybrid_dj_vj"] }),
      zones: vArray(vObject({ id: vString({ min: 1 }), label: vString({ min: 1 }), zoneType: vString({ enum: ["stage", "booth", "dancefloor", "crowd", "ceiling", "wall", "screen", "other"] }) })),
      fixtureGroups: vArray(vObject({ id: vString({ min: 1 }), label: vString({ min: 1 }), groupType: vString({}), zoneId: vString({ min: 1 }), capabilities: vArray(vString({})), simulationOnly: vBoolean, safetyNotes: vArray(vString({})) })),
      safetyProfile: vObject({ allowHighIntensity: vBoolean, allowStrobeSimulation: vBoolean, allowLaserSimulation: vBoolean, notes: vArray(vString({})) }),
    },
  )(v, path) as ValidationResult;
  const issues = [...base.issues];
  const rig = v as DJSEMVenueProductionRig;
  // every fixture group must be simulationOnly; capabilities must not name protocols
  for (const g of rig?.fixtureGroups ?? []) {
    if (g && g.simulationOnly !== true) issues.push({ path: `${path}.fixtureGroups`, message: `fixture group ${g.id} must be simulationOnly` });
    for (const c of g?.capabilities ?? []) if (PROTOCOL.test(c)) issues.push({ path: `${path}.fixtureGroups`, message: `capability '${c}' names a hardware protocol (not allowed)` });
  }
  return { valid: issues.length === 0, issues };
};

// ---- service (class-based; hard rule 12) ------------------------------------

/**
 * Resolves cue/chapter/semantic/stem/energy state into a renderer-neutral lighting runtime
 * state and dry-run adapter events. Simulation only: no DMX/Art-Net/sACN/laser/timecode/
 * hardware protocol output exists here. Safety always wins — emergency safe mode and
 * blackout override everything; high-intensity/laser needs venue permission.
 */
export class LightingSceneEngineSimulation {
  /**
   * Resolves the active lighting scene by priority: emergency safe mode > blackout > manual
   * cue > explicit cue > impact > stem onset > chapter > semantic > global-energy fallback
   * > idle/safe. Restricted scenes fall back to safe mode; high-intensity scenes are
   * clamped if the venue rig doesn't permit high intensity.
   * @param input - available scenes, optional venue rig, safety flags, and cue/chapter/semantic selectors.
   * @returns the resolved lighting runtime state.
   */
  resolveLightingState(input: ResolveLightingStateInput): DJSEMLightingRuntimeState {
    const at = input.resolvedAt ?? AT;
    if (input.emergencySafeMode) return this.resolveSafeMode({ resolvedAt: at, sessionId: input.sessionId, reason: "emergency safe mode" });
    if (input.blackout) return this.resolveBlackout({ resolvedAt: at, sessionId: input.sessionId });

    if (!input.scenes || input.scenes.length === 0) {
      return { stateId: "__idle__", sessionId: input.sessionId, resolvedAt: at, status: "safe", fixtureGroupStates: [], warnings: ["No lighting scenes available; safe idle fallback."], sourceTrace: { reason: "no scenes" } };
    }
    const byId = new Map(input.scenes.map((s) => [s.id, s]));
    const pick = (id?: string) => (id ? byId.get(id) : undefined);
    let scene: DJSEMLightingScene | undefined; let src = "global_energy";
    if (pick(input.manualCueSceneId)) { scene = pick(input.manualCueSceneId); src = "manual_cue"; }
    else if (pick(input.explicitCueSceneId)) { scene = pick(input.explicitCueSceneId); src = "explicit_cue"; }
    else if (input.impactEvent) { scene = this.firstBySafety(input.scenes); src = "impact"; }
    else if (input.stemOnset) { scene = this.firstBySafety(input.scenes); src = "stem_onset"; }
    else if (pick(input.chapterSceneId)) { scene = pick(input.chapterSceneId); src = "chapter"; }
    else if (input.semanticMatchIds?.length) { scene = input.semanticMatchIds.map((id) => byId.get(id)).find(Boolean); src = "semantic"; }
    if (!scene) { scene = this.firstBySafety(input.scenes); src = "global_energy_fallback"; }

    if (!scene) return this.resolveSafeMode({ resolvedAt: at, sessionId: input.sessionId, reason: "no resolvable scene" });

    const warnings: string[] = [];
    // Restricted scene → safe mode.
    if (scene.safetyLevel === "restricted") return this.resolveSafeMode({ resolvedAt: at, sessionId: input.sessionId, reason: `restricted scene ${scene.id}` });
    // High-intensity requires venue permission.
    let states = scene.fixtureGroupStates.map((s) => ({ ...s, intensity: clampUnit(s.intensity) }));
    if (scene.safetyLevel === "high_intensity" && input.rig && !input.rig.safetyProfile.allowHighIntensity) {
      warnings.push(`High-intensity scene ${scene.id} not permitted by venue ${input.rig.id}; intensities reduced.`);
      states = states.map((s) => ({ ...s, intensity: Math.min(s.intensity, 0.4) }));
    }
    let state: DJSEMLightingRuntimeState = {
      stateId: `ls_${scene.id}`, sessionId: input.sessionId, resolvedAt: at, status: "active",
      activeSceneId: scene.id, fixtureGroupStates: states, warnings,
      sourceTrace: { source: src, sceneId: scene.id, safetyLevel: scene.safetyLevel },
    };
    if (input.rig) state = this.applyVenueRig({ state, rig: input.rig });
    return state;
  }

  /**
   * @param input - optional session id and resolved-at timestamp.
   * @returns a runtime state with all fixture groups disabled (blackout).
   */
  resolveBlackout(input: ResolveBlackoutInput): DJSEMLightingRuntimeState {
    return { stateId: "__blackout__", sessionId: input.sessionId, resolvedAt: input.resolvedAt ?? AT, status: "blackout",
      fixtureGroupStates: [], warnings: [], sourceTrace: { reason: "blackout disables all fixture groups" } };
  }

  /**
   * @param input - optional session id, resolved-at timestamp, and safe-mode reason.
   * @returns a runtime state with all fixture groups disabled (safe hold).
   */
  resolveSafeMode(input: ResolveLightingSafeModeInput): DJSEMLightingRuntimeState {
    return { stateId: "__safe__", sessionId: input.sessionId, resolvedAt: input.resolvedAt ?? AT, status: "safe",
      fixtureGroupStates: [], warnings: [input.reason ?? "safe mode"], sourceTrace: { reason: input.reason ?? "safe mode overrides all automation" } };
  }

  /**
   * Filters a resolved lighting state against a venue rig: disables fixture groups not
   * present in the rig, and disables laser/strobe simulation groups the rig doesn't permit.
   * @param input - the resolved state and the venue rig to apply.
   * @returns the filtered state with warnings for any dropped/disabled groups.
   */
  applyVenueRig(input: ApplyVenueRigInput): DJSEMLightingRuntimeState {
    const { state, rig } = input;
    const known = new Map(rig.fixtureGroups.map((g) => [g.id, g]));
    const warnings = [...state.warnings];
    const states = state.fixtureGroupStates.filter((s) => {
      if (!known.has(s.fixtureGroupId)) { warnings.push(`Fixture group ${s.fixtureGroupId} not in venue ${rig.id}; dropped.`); return false; }
      const g = known.get(s.fixtureGroupId)!;
      if (g.groupType === "laser_simulation" && !rig.safetyProfile.allowLaserSimulation) { warnings.push(`Laser simulation ${s.fixtureGroupId} not permitted at venue ${rig.id}; disabled.`); return false; }
      if (g.groupType === "strobe" && !rig.safetyProfile.allowStrobeSimulation) { warnings.push(`Strobe simulation ${s.fixtureGroupId} not permitted; disabled.`); return false; }
      return true;
    }).map((s) => {
      const g = known.get(s.fixtureGroupId)!;
      return g.groupType === "laser_simulation" ? { ...s, effectMode: "laser_simulation_only" } : s;
    });
    return { ...state, fixtureGroupStates: states, warnings, sourceTrace: { ...state.sourceTrace, venueRig: rig.id } };
  }

  /**
   * Builds dry-run-only adapter-ready events (one summary event plus one per fixture group)
   * describing a resolved lighting state.
   * @param input - the resolved state and an optional creation timestamp.
   * @returns the generated adapter events, each carrying `dryRunOnly: true`.
   */
  createAdapterReadyEvents(input: CreateLightingAdapterEventsInput): DJSEMLightingAdapterEvent[] {
    const { state } = input; const createdAt = input.createdAt ?? AT;
    const evType = state.status === "blackout" ? "lighting.blackout.enabled" : state.status === "safe" ? "lighting.safe_mode.enabled" : "lighting.scene.resolved";
    const base: DJSEMLightingAdapterEvent = {
      eventId: `evt_${state.stateId}`, createdAt, eventType: evType, dryRunOnly: true,
      payload: { status: state.status, activeSceneId: state.activeSceneId, fixtureGroupStates: state.fixtureGroupStates },
      sourceTrace: { ...state.sourceTrace, note: "dry-run only; no hardware output" },
    };
    const perGroup: DJSEMLightingAdapterEvent[] = state.fixtureGroupStates.map((g) => ({
      eventId: `evt_${state.stateId}_${g.fixtureGroupId}`, createdAt, eventType: "lighting.fixture_group.updated", dryRunOnly: true,
      payload: g, sourceTrace: { note: "dry-run only" },
    }));
    return [base, ...perGroup];
  }

  /**
   * @param scene - the candidate lighting scene, of unknown shape.
   * @returns a validation result for the scene.
   */
  validateLightingScene(scene: unknown): DJSEMValidationResult {
    return toDJSEMValidationResult(vLightingSceneSim(scene), "lighting_scene");
  }
  /**
   * @param rig - the candidate venue production rig, of unknown shape.
   * @returns a validation result for the rig.
   */
  validateVenueRig(rig: unknown): DJSEMValidationResult {
    return toDJSEMValidationResult(vVenueRig(rig), "venue_rig");
  }

  private firstBySafety(scenes: DJSEMLightingScene[]): DJSEMLightingScene | undefined {
    // deterministic: safest first, then by id
    const rank: Record<DJSEMLightingScene["safetyLevel"], number> = { safe: 0, standard: 1, high_intensity: 2, restricted: 3 };
    return [...scenes].sort((a, b) => rank[a.safetyLevel] - rank[b.safetyLevel] || a.id.localeCompare(b.id))[0];
  }
}

// ---- default venue profiles -------------------------------------------------

const zone = (id: string, label: string, zoneType: DJSEMVenueZone["zoneType"]): DJSEMVenueZone => ({ id, label, zoneType });
const fg = (id: string, label: string, groupType: DJSEMFixtureGroupType, zoneId: string, caps: string[] = []): DJSEMFixtureGroup =>
  ({ id, label, groupType, zoneId, capabilities: caps, simulationOnly: true, safetyNotes: [] });

/** Default simulated venue production rigs, keyed by profile type. */
export const DEFAULT_VENUE_RIGS: Record<DJSEMVenueProductionRig["profileType"], DJSEMVenueProductionRig> = {
  bedroom_streamer: {
    id: "rig_bedroom", label: "Bedroom / Streamer Setup", profileType: "bedroom_streamer",
    zones: [zone("z_booth", "Booth", "booth"), zone("z_screen", "Screen", "screen")],
    fixtureGroups: [fg("wash_booth", "Booth Wash", "wash", "z_booth"), fg("led_screen", "Stream Screen", "led_wall", "z_screen")],
    safetyProfile: { allowHighIntensity: false, allowStrobeSimulation: false, allowLaserSimulation: false, notes: ["Home setup; conservative safety."] },
  },
  small_bar: {
    id: "rig_bar", label: "Small Bar", profileType: "small_bar",
    zones: [zone("z_stage", "Stage", "stage"), zone("z_floor", "Dancefloor", "dancefloor")],
    fixtureGroups: [fg("wash_stage", "Stage Wash", "wash", "z_stage"), fg("beam_floor", "Floor Beams", "beam", "z_floor"), fg("atmos", "Haze", "atmosphere", "z_floor")],
    safetyProfile: { allowHighIntensity: false, allowStrobeSimulation: true, allowLaserSimulation: false, notes: [] },
  },
  small_club: {
    id: "rig_club", label: "Small Club", profileType: "small_club",
    zones: [zone("z_stage", "Stage", "stage"), zone("z_floor", "Dancefloor", "dancefloor"), zone("z_ceiling", "Ceiling", "ceiling")],
    fixtureGroups: [fg("wash_stage", "Stage Wash", "wash", "z_stage"), fg("mh_ceiling", "Moving Heads", "moving_head", "z_ceiling"), fg("strobe_floor", "Strobes", "strobe", "z_floor"), fg("blinder", "Blinders", "blinder", "z_stage")],
    safetyProfile: { allowHighIntensity: true, allowStrobeSimulation: true, allowLaserSimulation: false, notes: [] },
  },
  projection_venue: {
    id: "rig_proj", label: "Projection Venue", profileType: "projection_venue",
    zones: [zone("z_wall", "Projection Wall", "wall"), zone("z_stage", "Stage", "stage")],
    fixtureGroups: [fg("proj_bright", "Projection Brightness", "projection_brightness", "z_wall"), fg("wash_stage", "Stage Wash", "wash", "z_stage")],
    safetyProfile: { allowHighIntensity: true, allowStrobeSimulation: false, allowLaserSimulation: false, notes: [] },
  },
  led_wall_venue: {
    id: "rig_led", label: "LED Wall Venue", profileType: "led_wall_venue",
    zones: [zone("z_wall", "LED Wall", "screen"), zone("z_floor", "Dancefloor", "dancefloor")],
    fixtureGroups: [fg("led_wall", "LED Wall", "led_wall", "z_wall"), fg("pixel_floor", "Pixel Floor", "pixel", "z_floor"), fg("crowd_wash", "Crowd Wash", "crowd", "z_floor")],
    safetyProfile: { allowHighIntensity: true, allowStrobeSimulation: true, allowLaserSimulation: false, notes: [] },
  },
  hybrid_dj_vj: {
    id: "rig_hybrid", label: "Hybrid DJ/VJ Venue", profileType: "hybrid_dj_vj",
    zones: [zone("z_stage", "Stage", "stage"), zone("z_wall", "Screen", "screen"), zone("z_floor", "Dancefloor", "dancefloor")],
    fixtureGroups: [fg("wash_stage", "Stage Wash", "wash", "z_stage"), fg("led_wall", "VJ Screen", "led_wall", "z_wall"), fg("mh_stage", "Moving Heads", "moving_head", "z_stage"), fg("laser_sim", "Laser (Simulation)", "laser_simulation", "z_stage", ["simulated_beam"])],
    safetyProfile: { allowHighIntensity: true, allowStrobeSimulation: true, allowLaserSimulation: true, notes: ["Laser is SIMULATION ONLY."] },
  },
};
/** {@link DEFAULT_VENUE_RIGS} as a flat array, for iterating in UI/tests. */
export const DEFAULT_VENUE_RIG_LIST: DJSEMVenueProductionRig[] = Object.values(DEFAULT_VENUE_RIGS);
